import { memo, useMemo } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { Launch } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import Loader from "../../UI/Loader";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Vimeo from "@u-wave/react-vimeo";
import { formatDate } from "../../../utils";
import { SOURCES_CUT_TEXT } from "../../../constants";
import styles from "../styles";

const useStyles = makeStyles(styles);

const renderers = {
	text: text => {
		// Check if the text contains bold and italic markers (***)
		if (/^\*\*\*.*\*\*\*$/.test(text)) {
			return <span style={{ fontWeight: 'bold', fontStyle: 'italic' }}>{text}</span>;
		}
		return text;
	}
};

const components = {
	a: ({ href, children }) => {
		if (children[0].startsWith('video:')) {
			const videoUrl = href;

			if (videoUrl.includes('vimeo.com')) {
				return (
					<Box sx={{ borderRadius: 1, overflow: 'hidden', mb: 2, mt: 1, border: '1px solid #ececec' }}>
						<Vimeo video={videoUrl} responsive height="430" controls />
					</Box>
				);
			}

			// YouTube Handling
			if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
				const videoId = videoUrl.split('v=')[1]?.split('&')[0] || videoUrl.split('/').pop();
				return (
					<iframe
						width="100%"
						height="315"
						src={`https://www.youtube.com/embed/${videoId}`}
						frameBorder="0"
						allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
						allowFullScreen
						title={children}
					/>
				);
			}

			// Direct Video Handling
			if (videoUrl.match(/\.(mp4|webm|ogg)$/)) {
				return (
					<video muted width="100%" controls>
						<source src={videoUrl} type={`video/${videoUrl.split('.').pop()}`} />
						Your browser does not support the video tag.
					</video>
				);
			}
		}
		return (
			<a href={href} target="_blank" rel="noopener noreferrer">
				{children} <Launch sx={{ verticalAlign: 'bottom', ml: 0.5 }} fontSize="small" />
			</a>
		);
	}
};

function TextElement(props) {
    const processedText = useMemo(() => {
    if (!props.text) return props.text;
    const sourcesIndex = props.text.indexOf(SOURCES_CUT_TEXT);
    return sourcesIndex !== -1
      ? props.text.substring(0, sourcesIndex).trim()
      : props.text;
  }, [props.text]);
  return (
    <ReactMarkdown
      renderers={renderers}
			components={components}
      remarkPlugins={[remarkGfm]}
    >
      {processedText}
    </ReactMarkdown>
  );
}

function ButtonElement(props) {
  function handleChipClick() {
    console.log("Button clicked");
  }

  return (
    <Chip
      onClick={() => handleChipClick()}
      label={props.label}
      variant="outlined"
      clickable
    />
  );
}

function Element({ data }) {
  switch (data.element) {
    case "text":
      return data.text === "..." ? <Loader /> : <TextElement {...data} />;
    case "image":
      return <img src={data.url} alt={data.alt_text} />;
    case ("action_button", "link_button", "email_button"):
      return <ButtonElement />;
    default:
      return null;
  }
}

function ChatBubble(props) {
  const classes = useStyles();
  const payload = props?.message?.payload
    ? JSON.parse(props.message.payload)
    : null;

  return (
    <Box
      sx={{ mt: 1.3, mb: 1.3 }}
      alignItems="flex-start"
      display="flex"
      flexWrap="wrap"
      key={props.message.index}
    >
      <Box className={classes.botMessage}>
        {props?.message?.payload ? (
          <>
            <Box className={classes.botMessageContent}>
              {payload?.message?.elements?.map((item) => (
                <Element data={item} />
              ))}
            </Box>
          </>
        ) : null}
        <Typography className={classes.messageTimestamp} variant="caption">
          {props.message?.timestamp &&
            formatDate(props.message?.timestamp, true)}
        </Typography>
      </Box>
    </Box>
  );
}

export default memo(
  ChatBubble,
  (prevProps, nextProps) =>
    JSON.stringify(prevProps?.message) === JSON.stringify(nextProps?.message)
);
